import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { doLogin, doRegistration } from "../../services/LoginService";
import { loginSuccess } from "../../authentication/AuthActions";
import AlertSnakBar from "../dialogs/alert-message/AlertSnakBar";
import Modal from "../modal/modal.tsx";
import "./login.css";

interface LoginProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin?: () => void;
}

const Login = ({ isOpen, onClose, onLogin }: LoginProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState<"login" | "register">("login");

  // Login form states
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // Register form states
  const [registerUsername, setRegisterUsername] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [alertOpen, setAlertOpen] = useState({ open: false, type: "", message: "" });
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    doLogin({ username, password })
      .then((res: any) => {
        setAlertOpen({ open: true, type: "success", message: "Logged in successfully" });
        setTimeout(() => {
          dispatch(loginSuccess(res.data));
          if (onLogin) {
            onLogin();
          } else {
            navigate("/");
          }
          onClose();
        }, 1500);
      })
      .catch((err: any) => {
        setAlertOpen({ open: true, type: "error", message: err.response?.data?.message || "Login failed" });
      });
  };

  const handleRegisterSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords match
    if (registerPassword !== confirmPassword) {
      setAlertOpen({ open: true, type: "error", message: "Passwords do not match" });
      return;
    }

    // Validate email format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(registerUsername)) {
      setAlertOpen({
        open: true,
        type: "error",
        message: "Not valid email`"
      });
      return;
    }

    
    // Validate password length
    if (registerPassword.length < 6) {
      setAlertOpen({ open: true, type: "error", message: "Password must be at least 6 characters long" });
      return;
    }

    const registrationData = {
      username: registerUsername,
      password: registerPassword,
      confirmPassword
    };

    doRegistration(registrationData)
      .then((res: any) => {
        setAlertOpen({ open: true, type: "success", message: "Registration successful! Please sign in." });
        setTimeout(() => {
          setActiveTab("login");
          // Clear registration form
          setRegisterUsername("");
          setRegisterPassword("");
          setConfirmPassword("");
        }, 1500);
      })
      .catch((err: any) => {
        setAlertOpen({
          open: true,
          type: "error",
          message: err.response?.data?.message || "Registration failed"
        });
      });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="login-overlay" onClick={onClose}>
        <div className="login-wrapper">
          <button className="login-close" onClick={onClose}>
            ×
          </button>
          <div className="login-modal" onClick={(e) => e.stopPropagation()}>

            <div className="login-tabs">
              <button
                className={`login-tab ${activeTab === "login" ? "active" : ""}`}
                onClick={() => setActiveTab("login")}
              >
                Sign In
              </button>
              <button
                className={`login-tab ${activeTab === "register" ? "active" : ""}`}
                onClick={() => setActiveTab("register")}
              >
                Register
              </button>
            </div>

            <div className="login-content">
              {activeTab === "login" ? (
                <>
                  <h2 className="login-title">Sign In</h2>
                  <form className="login-form" onSubmit={handleSubmit}>
                    <div className="login-input-group">
                      <input
                        type="text"
                        placeholder="Insert your email *"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="login-input"
                        required
                      />
                    </div>

                    <div className="login-input-group">
                      <input
                        type="password"
                        placeholder="Insert your password *"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="login-input"
                        required
                      />
                    </div>

                    <button type="submit" className="login-submit-button">
                      Sign In →
                    </button>

                    <p className="login-switch-prompt">
                      Don't have an account?{" "}
                      <button
                        type="button"
                        className="login-switch-link"
                        onClick={() => setActiveTab("register")}
                      >
                        Register for free
                      </button>
                    </p>
                  </form>
                </>
              ) : (
                <>
                  <h2 className="login-title">Register</h2>
                  <form className="login-form" onSubmit={handleRegisterSubmit}>
                    <p className="login-switch-prompt">
                      Your{" "}
                      <button
                        type="button"
                        className="login-switch-link"
                        onClick={() => setShowPrivacyModal(true)}
                      >
                        privacy
                      </button>{" "}
                      is important to us.
                    </p>

                    <div className="login-input-group">
                      <input
                        type="text"
                        placeholder="Email *"
                        value={registerUsername}
                        onChange={(e) => setRegisterUsername(e.target.value)}
                        className="login-input"
                        required
                      />
                    </div>

                    <div className="login-input-group">
                      <input
                        type="password"
                        placeholder="Password (min 6 characters) *"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        className="login-input"
                        required
                      />
                    </div>

                    <div className="login-input-group">
                      <input
                        type="password"
                        placeholder="Confirm Password *"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="login-input"
                        required
                      />
                    </div>

                    <button type="submit" className="login-submit-button">
                      Register →
                    </button>

                    <p className="login-switch-prompt">
                      Already have an account?{" "}
                      <button
                        type="button"
                        className="login-switch-link"
                        onClick={() => setActiveTab("login")}
                      >
                        Sign in, instead
                      </button>
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
          <AlertSnakBar aleartOpen={alertOpen} setAlertOpen={setAlertOpen} />
        </div>
      </div>
      <Modal
        type="privacy"
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </>
  );
};

export default Login;
